/**
 * Split Payment System V2 - Improved UX with per-split payment selection
 * Handles the new split payment interface with better payment method management
 */

class SplitPaymentSystemV2 {
  constructor() {
    this.modal = document.getElementById('split-payment-modal');
    this.cartTotal = 0;
    this.originalCartTotal = 0; // Store original total before discount
    this.discountAmount = 0;
    this.activeDiscountCode = null;
    this.numberOfSplits = 0;
    this.splitConfigurations = [];
    this.usedBNPLProviders = new Set(); // Track BNPL providers already used
    this.sessionKey = 'split_payment_session';
    this.currency = 'GBP'; // Default currency
    this.currencySymbol = '£'; // Default symbol
    this.apiCredentials = {
      apiKey: 'f0a6f0a458874992a796541903441605',
      storefrontToken: '107e40eef6eb6c9db6cd4bdf75b3e4dd'
    };

    this.init();
  }

  init() {
    console.log('[Split Payment V2] Initializing...');

    // Get cart total
    this.cartTotal = this.getCartTotal();
    console.log('[Split Payment V2] Cart total:', this.cartTotal);

    // Bind events
    this.bindEvents();

    // Check for existing session
    this.checkExistingSession();
  }

  bindEvents() {
    console.log('[Split Payment V2] Binding events...');

    // Use event delegation for the split payment button since it's in the cart drawer
    // This ensures it works even if the button is added dynamically
    document.addEventListener('click', (e) => {
      const splitButton = e.target.closest('.split-payment-button');
      if (splitButton) {
        console.log('[Split Payment V2] Split payment button clicked');
        e.preventDefault();
        this.openModal();
      }
    });

    console.log('[Split Payment V2] Event delegation set up for split payment button');

    // Modal close button
    const closeBtn = this.modal?.querySelector('.split-modal-close');
    const cancelBtn = document.getElementById('cancel-split-btn');

    closeBtn?.addEventListener('click', () => this.closeModal());
    cancelBtn?.addEventListener('click', () => this.closeModal());

    // Modal overlay click
    const overlay = this.modal?.querySelector('.split-modal-overlay');
    overlay?.addEventListener('click', () => this.closeModal());

    // Number of splits selection
    const splitButtons = document.querySelectorAll('.split-btn');
    splitButtons.forEach(btn => {
      btn.addEventListener('click', (e) => this.selectNumberOfSplits(e));
    });

    // Auto-calculate button - REMOVED (no longer needed)

    // Proceed button
    const proceedBtn = document.getElementById('proceed-split-btn');
    proceedBtn?.addEventListener('click', () => this.proceedWithSplit());

    // Discount code functionality
    const applyDiscountBtn = document.getElementById('apply-discount-btn');
    const discountInput = document.getElementById('split-discount-code');
    const removeDiscountBtn = document.querySelector('.remove-discount');

    applyDiscountBtn?.addEventListener('click', () => this.applyDiscountCode());
    discountInput?.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') this.applyDiscountCode();
    });
    removeDiscountBtn?.addEventListener('click', () => this.removeDiscountCode());
  }

  getCartTotal() {
    // Get cart total from data attribute or DOM
    const cartContainer = document.querySelector('[data-cart-total]');
    if (cartContainer) {
      return parseFloat(cartContainer.dataset.cartTotal) / 100;
    }

    // Try the split-payment-container
    const splitContainer = document.querySelector('.split-payment-container');
    if (splitContainer && splitContainer.dataset.cartTotal) {
      return parseFloat(splitContainer.dataset.cartTotal) / 100;
    }

    // Fallback: parse from displayed text
    const totalText = document.querySelector('.cart__total-value')?.textContent ||
                     document.querySelector('.cart__footer-total')?.textContent;
    if (totalText) {
      return parseFloat(totalText.replace(/[^0-9.]/g, ''));
    }

    return 0;
  }

  async openModal() {
    // Close cart drawer first
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && cartDrawer.close) {
      cartDrawer.close();
    }
    const drawerDialog = document.querySelector('.cart-drawer__dialog');
    if (drawerDialog && drawerDialog.open) {
      drawerDialog.close();
    }
    const drawer = document.querySelector('[open].cart-drawer__dialog');
    if (drawer) {
      drawer.removeAttribute('open');
    }

    // Reset modal state FIRST to clear any old data
    this.resetModal();

    // Detect available payment methods based on user's country
    await this.initializeAvailablePaymentMethods();

    // Fetch latest cart data
    console.log('[Split Payment V2] Fetching fresh cart data...');
    const cartData = await this.getCartData();

    // CRITICAL: Update all totals with fresh data from cart.js (prices are in cents)
    this.cartTotal = (cartData.total_price || 0) / 100;
    this.originalCartTotal = ((cartData.total_price || 0) + (cartData.total_discount || 0)) / 100;
    this.discountAmount = (cartData.total_discount || 0) / 100;

    // Extract currency information
    if (cartData.currency) {
      this.currency = cartData.currency;
      console.log('[Split Payment V2] Currency detected:', this.currency);
    }

    console.log('[Split Payment V2] Fresh cart data loaded:');
    console.log('  - Original price:', this.formatMoney(this.originalCartTotal));
    console.log('  - Discount amount:', this.formatMoney(this.discountAmount));
    console.log('  - Final total:', this.formatMoney(this.cartTotal));

    // Small delay to ensure cart closes before modal opens
    setTimeout(() => {
      this.modal.style.display = 'block';
      document.body.style.overflow = 'hidden';

      // Update cart total display with fresh data
      const totalElement = document.getElementById('split-cart-total');
      if (totalElement) {
        totalElement.textContent = this.formatMoney(this.cartTotal);
      }

      // Populate customer note if exists
      const customerNoteField = document.getElementById('split-customer-note');
      if (customerNoteField && cartData.note) {
        customerNoteField.value = cartData.note;
        console.log('[Split Payment V2] Pre-populated customer note:', cartData.note);
      }

      // Check for discount codes in fresh cart data
      if (cartData.discount_codes && cartData.discount_codes.length > 0) {
        this.activeDiscountCode = cartData.discount_codes[0];
        this.displayActiveDiscount(this.activeDiscountCode);

        // Update discount display if discount exists
        if (this.discountAmount > 0) {
          this.updateDiscountDisplay();
        }
      } else {
        // No discount in current cart - clear any old display
        this.activeDiscountCode = null;
        const activeDiscountEl = document.getElementById('active-discount');
        if (activeDiscountEl) {
          activeDiscountEl.style.display = 'none';
        }
      }
    }, 300);
  }

  closeModal() {
    this.modal.style.display = 'none';
    document.body.style.overflow = '';
    this.resetModal();
  }

  resetModal() {
    // Reset all selections
    document.querySelectorAll('.split-btn').forEach(btn => btn.classList.remove('active'));
    document.getElementById('split-configurations').style.display = 'none';
    document.getElementById('split-forms-container').innerHTML = '';
    document.getElementById('split-preview').style.display = 'none';
    document.getElementById('proceed-split-btn').disabled = true;
    this.numberOfSplits = 0;
    this.splitConfigurations = [];
    this.usedBNPLProviders.clear();

    // Clear discount displays
    const discountInput = document.getElementById('split-discount-code');
    if (discountInput) {
      discountInput.value = '';
      discountInput.placeholder = 'Enter discount code';
    }

    // Clear error messages
    const errorDiv = document.querySelector('.split-error-message');
    if (errorDiv) {
      errorDiv.style.display = 'none';
      errorDiv.textContent = '';
    }
  }

  selectNumberOfSplits(e) {
    const button = e.target;
    const splits = parseInt(button.dataset.splits);

    // Update active state
    document.querySelectorAll('.split-btn').forEach(btn => btn.classList.remove('active'));
    button.classList.add('active');

    this.numberOfSplits = splits;

    // Show configurations section
    document.getElementById('split-configurations').style.display = 'block';

    // Generate split forms
    this.generateSplitForms(splits);
  }

  generateSplitForms(numberOfSplits) {
    const container = document.getElementById('split-forms-container');
    const template = document.getElementById('split-form-template');

    if (!template) {
      console.error('[Split Payment V2] Split form template not found!');
      return;
    }

    container.innerHTML = '';
    this.splitConfigurations = [];
    this.usedBNPLProviders.clear();

    for (let i = 1; i <= numberOfSplits; i++) {
      const clone = template.content.cloneNode(true);
      const formItem = clone.querySelector('.split-form-item');

      formItem.dataset.splitNumber = i;
      formItem.querySelector('.split-number').textContent = i;

      // Update payment method dropdown with available options
      const methodSelect = formItem.querySelector('.split-payment-method');
      if (methodSelect && this.availablePaymentMethods) {
        methodSelect.innerHTML = this.generatePaymentOptions();
      }

      // Add event listeners
      const amountInput = formItem.querySelector('.split-amount');

      methodSelect.addEventListener('change', (e) => this.onMethodChange(e, i));
      amountInput.addEventListener('input', () => this.updateSplitPreview());

      container.appendChild(clone);
    }

    // Show preview
    document.getElementById('split-preview').style.display = 'block';
    this.updateSplitPreview();
  }

  async initializeAvailablePaymentMethods() {
    // Show loading state
    console.log('[Split Payment V2] Detecting available payment methods...');

    try {
      // Get available methods from API
      if (window.splitPaymentAPI) {
        this.availablePaymentMethods = await window.splitPaymentAPI.getAvailablePaymentMethods();
        console.log('[Split Payment V2] Available payment methods:', this.availablePaymentMethods);
      } else {
        // Fallback to all methods if API not available
        console.log('[Split Payment V2] API not available, using all payment methods');
        this.availablePaymentMethods = [
          { type: 'card', name: 'Credit/Debit Card', available: true },
          { type: 'paypal', name: 'PayPal', available: true },
          { type: 'klarna', name: 'Klarna', available: true },
          { type: 'clearpay', name: 'Clearpay/Afterpay', available: true }
        ];
      }
    } catch (error) {
      console.error('[Split Payment V2] Error detecting payment methods:', error);
      // Fallback to all methods on error
      this.availablePaymentMethods = [
        { type: 'card', name: 'Credit/Debit Card', available: true },
        { type: 'paypal', name: 'PayPal', available: true },
        { type: 'klarna', name: 'Klarna', available: true },
        { type: 'clearpay', name: 'Clearpay/Afterpay', available: true }
      ];
    }
  }

  generatePaymentOptions() {
    // Generate options based on available payment methods
    let optionsHTML = '<option value="">Select payment method...</option>';

    this.availablePaymentMethods.forEach(method => {
      if (method.available) {
        const multiple = method.type === 'card' ? 'true' : 'false';
        const displayName = method.name + (method.requiresLimit ? ' (Buy Now Pay Later)' : '');
        optionsHTML += `<option value="${method.type}" data-multiple="${multiple}">${displayName}</option>`;
      }
    });

    return optionsHTML;
  }

  onMethodChange(e, splitNumber) {
    const select = e.target;
    const selectedValue = select.value;
    const formItem = select.closest('.split-form-item');
    const bnplLimitInput = formItem.querySelector('.bnpl-limit-input');

    // Show/hide BNPL limit input
    if (['klarna', 'clearpay'].includes(selectedValue)) {
      bnplLimitInput.style.display = 'block';

      // Update the check limit link based on provider
      const checkLink = bnplLimitInput.querySelector('.check-limit-link');
      const recommendationText = bnplLimitInput.querySelector('.bnpl-recommendation-text');
      if (selectedValue === 'klarna') {
        checkLink.href = 'https://app.klarna.com/login';
        checkLink.textContent = 'Check your Klarna limit →';
        recommendationText.textContent = 'We recommend you check your credit limit on Klarna before proceeding';
      } else {
        checkLink.href = 'https://portal.clearpay.co.uk/uk/login-email';
        checkLink.textContent = 'Check your Clearpay limit →';
        recommendationText.textContent = 'We recommend you check your credit limit on Clearpay before proceeding';
      }
    } else {
      bnplLimitInput.style.display = 'none';
    }

    // Update available options in other selects
    this.updateAvailableOptions();
    this.updateSplitPreview();
  }

  updateAvailableOptions() {
    this.usedBNPLProviders.clear();

    // First pass: collect all selected BNPL providers
    document.querySelectorAll('.split-payment-method').forEach(select => {
      const value = select.value;
      if (['paypal', 'klarna', 'clearpay', 'giftcard'].includes(value)) {
        this.usedBNPLProviders.add(value);
      }
    });

    // Second pass: disable/enable options
    document.querySelectorAll('.split-payment-method').forEach(select => {
      const currentValue = select.value;

      select.querySelectorAll('option').forEach(option => {
        const optionValue = option.value;
        const allowMultiple = option.dataset.multiple === 'true';

        // Disable if it's a BNPL/single-use provider that's already used elsewhere
        if (!allowMultiple && this.usedBNPLProviders.has(optionValue) && currentValue !== optionValue) {
          option.disabled = true;
          option.textContent = option.textContent.replace(' (Already selected)', '') + ' (Already selected)';
        } else {
          option.disabled = false;
          option.textContent = option.textContent.replace(' (Already selected)', '');
        }
      });
    });
  }

  updateSplitPreview() {
    const previewList = document.getElementById('split-preview-list');
    const remainingElement = document.getElementById('remaining-amount');
    const errorDiv = document.getElementById('split-error-messages');

    this.splitConfigurations = [];
    let totalAllocated = 0;
    let previewHTML = '';

    // Collect all split configurations
    document.querySelectorAll('.split-form-item').forEach((formItem, index) => {
      const method = formItem.querySelector('.split-payment-method').value;
      const amount = parseFloat(formItem.querySelector('.split-amount').value) || 0;

      if (method) {
        this.splitConfigurations.push({
          splitNumber: index + 1,
          method: method,
          type: method,  // Add 'type' for compatibility with Reactflow code
          amount: amount,
          methodName: this.getMethodName(method),
          provider: this.getMethodName(method)  // Add provider name for draft order notes
        });

        totalAllocated += amount;
        previewHTML += `
          <div class="split-preview-item">
            <span>Payment ${index + 1}: ${this.getMethodName(method)}</span>
            <span>${this.formatMoney(amount)}</span>
          </div>
        `;
      }
    });

    previewList.innerHTML = previewHTML || '<p style="color: #999;">Select payment methods and enter amounts</p>';

    const remaining = this.cartTotal - totalAllocated;
    remainingElement.textContent = this.formatMoney(remaining);

    // Clear previous errors
    errorDiv.style.display = 'none';
    errorDiv.textContent = '';

    // Update button state and show helpful messages
    const proceedBtn = document.getElementById('proceed-split-btn');
    let canProceed = true;
    let errorMessage = '';

    if (this.splitConfigurations.length !== this.numberOfSplits) {
      canProceed = false;
      errorMessage = `Please configure all ${this.numberOfSplits} payment splits`;
    } else if (Math.abs(remaining) > 0.01) {
      canProceed = false;
      if (remaining > 0) {
        errorMessage = `Please allocate the remaining ${this.formatMoney(remaining)}`;
      } else {
        errorMessage = `Total exceeds cart amount by ${this.formatMoney(Math.abs(remaining))}`;
      }
    }

    if (canProceed) {
      proceedBtn.disabled = false;
      remainingElement.parentElement.style.color = 'green';
      console.log('[Split Payment V2] Ready to proceed - button enabled');
    } else {
      proceedBtn.disabled = true;
      remainingElement.parentElement.style.color = remaining === 0 ? 'green' : (remaining > 0 ? 'orange' : 'red');

      if (errorMessage) {
        errorDiv.textContent = errorMessage;
        errorDiv.style.display = 'block';
      }
    }
  }

  autoCalculateSplit() {
    if (this.numberOfSplits === 0) {
      this.showError('Please select number of payments first');
      return;
    }

    // Get all configured methods
    const forms = document.querySelectorAll('.split-form-item');
    let hasAllMethods = true;

    forms.forEach(form => {
      const method = form.querySelector('.split-payment-method').value;
      if (!method) hasAllMethods = false;
    });

    if (!hasAllMethods) {
      this.showError('Please select payment methods for all splits first');
      return;
    }

    // Auto-calculate amounts
    let remaining = this.cartTotal;
    const splits = Array.from(forms);

    // First pass: handle gift cards and BNPL with limits
    splits.forEach(form => {
      const method = form.querySelector('.split-payment-method').value;
      const amountInput = form.querySelector('.split-amount');

      if (method === 'giftcard') {
        const maxGiftCard = parseFloat(form.querySelector('.split-payment-method option[value="giftcard"]')?.dataset.max) || 0;
        const allocation = Math.min(remaining, maxGiftCard);
        amountInput.value = allocation.toFixed(2);
        remaining -= allocation;
      } else if (['klarna', 'clearpay'].includes(method)) {
        const limitInput = form.querySelector('.bnpl-limit');
        const limit = parseFloat(limitInput.value) || 0;

        if (limit > 0) {
          const allocation = Math.min(remaining, limit);
          amountInput.value = allocation.toFixed(2);
          remaining -= allocation;
        }
      }
    });

    // Second pass: distribute remaining among other methods
    const unallocatedSplits = splits.filter(form => {
      const amount = parseFloat(form.querySelector('.split-amount').value) || 0;
      return amount === 0;
    });

    if (unallocatedSplits.length > 0 && remaining > 0) {
      const perSplit = remaining / unallocatedSplits.length;
      unallocatedSplits.forEach(form => {
        form.querySelector('.split-amount').value = perSplit.toFixed(2);
      });
    }

    this.updateSplitPreview();
  }

  async proceedWithSplit() {
    // Validate configuration
    if (!this.validateSplitConfiguration()) {
      return;
    }

    // Save customer note to cart if provided
    const customerNoteField = document.getElementById('split-customer-note');
    if (customerNoteField) {
      const customerNote = customerNoteField.value.trim();
      if (customerNote) {
        console.log('[Split Payment V2] Saving customer note to cart:', customerNote);
        await this.updateCartNote(customerNote);
      }
    }

    // Save configuration to session
    this.saveSplitSession();

    // Show loading state
    this.showLoading();

    try {
      // Create draft orders via API
      const response = await this.createDraftOrders();

      // Check if we got a pending status (Reactflow processing)
      if (response && (response.status === 'processing' || response.status === 'pending')) {
        console.log('[Split Payment V2] Webhook processing, polling for data...');
        console.log('[Split Payment V2] Response:', response);

        // Show processing message
        this.showSuccess('Creating split payments... Please wait...');

        // Poll for the callback data
        const groupId = response.groupId || this.groupId;
        console.log('[Split Payment V2] Using groupId for polling:', groupId);

        const callbackData = await this.pollForCallbackData(groupId);

        if (callbackData && callbackData.success) {
          // Use the callback data as the response
          response.success = callbackData.success;
          response.draftOrders = callbackData.draftOrders;
          response.summary = callbackData.summary;
        } else {
          // Polling failed, show error
          this.showError('Failed to create split payments. Please try again.');
          this.hideLoading();
          return;
        }
      }

      if (response.success) {
        // Store draft order IDs
        sessionStorage.setItem('split_draft_orders', JSON.stringify(response.draftOrders));

        // Check if we should show confirmation modal
        if (response.showConfirmation && response.draftOrders && response.draftOrders.length > 0) {
          this.showConfirmationModal(response);
        } else {
          // Fallback: Show success message and redirect
          this.showSuccess(response.message || 'Split payment configuration saved!');

          // Redirect to progress page
          setTimeout(() => {
            sessionStorage.setItem('just_created_split', 'true');
            sessionStorage.setItem('current_split_group_id', response.groupId);
            window.location.href = `/pages/split-payment-progress?group_id=${response.groupId}`;
          }, 2000);
        }
      } else {
        this.showError(response.error || 'Failed to create split orders. Please try again.');
        this.hideLoading();
      }
    } catch (error) {
      console.error('Split payment error:', error);
      this.showError('An error occurred. Please try again.');
      this.hideLoading();
    }
  }

  async createDraftOrders() {
    const cartData = await this.getCartData();

    // Use the API handler (loaded separately)
    if (window.splitPaymentAPI) {
      return await window.splitPaymentAPI.createDraftOrders(this.splitConfigurations, cartData);
    } else {
      // Fallback: Save to cart attributes and redirect to checkout
      const attributes = {
        attributes: {
          'split_payment': 'true',
          'split_config': JSON.stringify(this.splitConfigurations),
          'split_count': this.numberOfSplits.toString()
        }
      };

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(attributes)
      });

      return {
        success: true,
        firstPaymentUrl: '/checkout',
        message: 'Split configuration saved. Proceeding to checkout...'
      };
    }
  }

  async getCartData() {
    // Fetch current cart data from Shopify
    const response = await fetch('/cart.js');
    const cart = await response.json();

    // Preserve original customer note
    if (cart.note) {
      cart.original_customer_note = cart.note;
      console.log('[Split Payment V2] Preserving customer note:', cart.note);
    }

    // Capture discount codes if present
    if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
      console.log('[Split Payment V2] Found discount applications:', cart.cart_level_discount_applications);
      // Extract discount codes
      cart.discount_codes = cart.cart_level_discount_applications
        .filter(d => d.type === 'discount_code')
        .map(d => d.title || d.code);
    }

    // Also check for the total discount amount
    if (cart.total_discount && cart.total_discount > 0) {
      console.log('[Split Payment V2] Total discount amount:', cart.total_discount);
    }

    // Try to get customer data if logged in
    // Using same method as wishlist-manager.js which successfully gets customer ID
    try {
      // Method 1: Check meta tag (most reliable)
      const customerMeta = document.querySelector('meta[name="customer-id"], meta[name="current-customer-id"]');
      if (customerMeta?.content) {
        cart.customer_id = customerMeta.content;
        console.log('[Split Payment V2] Found customer ID from meta tag:', cart.customer_id);
      }

      // Method 2: Check ShopifyAnalytics (fallback)
      if (!cart.customer_id && window.ShopifyAnalytics?.meta?.page?.customerId) {
        cart.customer_id = window.ShopifyAnalytics.meta.page.customerId;
        console.log('[Split Payment V2] Found customer ID from ShopifyAnalytics:', cart.customer_id);
      }

      // Method 3: Check window.customerId
      if (!cart.customer_id && window.customerId) {
        cart.customer_id = window.customerId;
        console.log('[Split Payment V2] Found customer ID from window.customerId:', cart.customer_id);
      }

      // Method 4: Check __st object
      if (!cart.customer_id && window.__st?.cid) {
        cart.customer_id = window.__st.cid;
        console.log('[Split Payment V2] Found customer ID from __st:', cart.customer_id);
      }

      // Method 5: Check data attributes
      if (!cart.customer_id) {
        const customerElement = document.querySelector('[data-customer-id]');
        if (customerElement?.dataset?.customerId) {
          cart.customer_id = customerElement.dataset.customerId;
          console.log('[Split Payment V2] Found customer ID from data attribute:', cart.customer_id);
        }
      }

      // Method 6: Check window.meta
      if (!cart.customer_id && window.meta?.page?.customerId) {
        cart.customer_id = window.meta.page.customerId;
        console.log('[Split Payment V2] Found customer ID from window.meta:', cart.customer_id);
      }

      // Log authentication status
      const isAuthenticated = cart.customer_id || document.cookie.includes('_shopify_customer_token');
      console.log('[Split Payment V2] Authentication status:', isAuthenticated, 'Customer ID:', cart.customer_id);

      // Try to get customer email if we have customer ID
      if (cart.customer_id && !cart.email) {
        // Check for email in meta tags
        const emailMeta = document.querySelector('meta[name="customer-email"], meta[name="current-customer-email"]');
        if (emailMeta?.content) {
          cart.email = emailMeta.content;
          console.log('[Split Payment V2] Found customer email from meta tag:', cart.email);
        }

        // Check for email in DOM elements
        if (!cart.email) {
          const emailElement = document.querySelector('[data-customer-email], .customer-email');
          if (emailElement) {
            cart.email = emailElement.dataset?.customerEmail || emailElement.textContent?.trim();
            console.log('[Split Payment V2] Found customer email from DOM:', cart.email);
          }
        }
      }

    } catch (error) {
      console.log('[Split Payment V2] Error getting customer data:', error);
    }

    console.log('[Split Payment V2] Final cart data with customer info:', {
      has_items: cart.items?.length > 0,
      customer_id: cart.customer_id,
      email: cart.email,
      item_count: cart.item_count,
      total_price: cart.total_price
    });

    return cart;
  }

  validateSplitConfiguration() {
    const total = this.splitConfigurations.reduce((sum, config) => sum + config.amount, 0);
    const diff = Math.abs(this.cartTotal - total);

    if (diff > 0.01) {
      this.showError('Split amounts must equal the cart total');
      return false;
    }

    if (this.splitConfigurations.length !== this.numberOfSplits) {
      this.showError(`Please configure all ${this.numberOfSplits} payment splits`);
      return false;
    }

    // Check that all splits have methods and amounts
    for (const config of this.splitConfigurations) {
      if (!config.method || config.amount <= 0) {
        this.showError('All splits must have a payment method and amount');
        return false;
      }
    }

    return true;
  }

  saveSplitSession() {
    const session = {
      timestamp: Date.now(),
      splits: this.numberOfSplits,
      configurations: this.splitConfigurations,
      cartTotal: this.cartTotal,
      completed: []
    };

    sessionStorage.setItem(this.sessionKey, JSON.stringify(session));
  }

  checkExistingSession() {
    const session = sessionStorage.getItem(this.sessionKey);
    if (session) {
      const data = JSON.parse(session);

      // Check if session is less than 24 hours old
      const hoursSinceCreated = (Date.now() - data.timestamp) / (1000 * 60 * 60);
      if (hoursSinceCreated < 24 && data.completed.length > 0) {
        // Show continue option
        this.showContinueOption(data);
      }
    }
  }

  showContinueOption(session) {
    // Add a banner to cart page
    const banner = document.createElement('div');
    banner.className = 'split-payment-continue-banner';
    banner.innerHTML = `
      <p>You have an incomplete split payment (${session.completed.length}/${session.splits} completed)</p>
      <button onclick="splitPaymentV2.continueSession()">Continue Payment</button>
      <button onclick="splitPaymentV2.clearSession()">Start Over</button>
    `;

    document.querySelector('.cart__footer')?.prepend(banner);
  }

  continueSession() {
    const session = JSON.parse(sessionStorage.getItem(this.sessionKey));
    const nextPaymentIndex = session.completed.length;
    const draftOrders = JSON.parse(sessionStorage.getItem('split_draft_orders'));

    if (draftOrders && draftOrders[nextPaymentIndex]) {
      // Open in new tab so user can come back to progress page
      window.open(draftOrders[nextPaymentIndex].invoiceUrl, '_blank');
      // Also navigate to progress page
      window.location.href = `/pages/split-payment-progress?group_id=${session.groupId}`;
    }
  }

  clearSession() {
    sessionStorage.removeItem(this.sessionKey);
    sessionStorage.removeItem('split_draft_orders');
    document.querySelector('.split-payment-continue-banner')?.remove();
  }

  getMethodName(type) {
    const names = {
      'giftcard': 'Gift Card',
      'card': 'Credit/Debit Card',
      'paypal': 'PayPal',
      'klarna': 'Klarna',
      'clearpay': 'Clearpay'
    };
    return names[type] || type;
  }

  formatMoney(amount) {
    // Use the cart's currency, fallback to GBP if not set
    const currencyCode = this.currency || 'GBP';

    // Determine locale based on currency for better formatting
    const localeMap = {
      'GBP': 'en-GB',
      'USD': 'en-US',
      'EUR': 'en-EU',
      'CAD': 'en-CA',
      'AUD': 'en-AU',
      'NZD': 'en-NZ'
    };
    const locale = localeMap[currencyCode] || 'en-US';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode
    }).format(amount);
  }

  showError(message) {
    const errorDiv = document.getElementById('split-error-messages');
    errorDiv.textContent = message;
    errorDiv.style.display = 'block';

    setTimeout(() => {
      errorDiv.style.display = 'none';
    }, 5000);
  }

  showSuccess(message) {
    const errorDiv = document.getElementById('split-error-messages');
    errorDiv.textContent = message;
    errorDiv.style.display = 'block';
    errorDiv.style.background = '#d4edda';
    errorDiv.style.color = '#155724';
    errorDiv.style.borderColor = '#c3e6cb';
  }

  showConfirmationModal(response) {
    // Hide loading state
    this.hideLoading();

    // Create modal HTML
    const modalHTML = `
      <div id="split-payment-confirmation-modal" class="split-payment-modal" style="display: block;">
        <div class="split-payment-modal-overlay"></div>
        <div class="split-payment-modal-content" style="max-width: 600px;">
          <div class="split-payment-modal-header">
            <h2 style="color: #4CAF50;">✅ ${response.draftOrders.length} Split Orders Created Successfully!</h2>
          </div>

          <div class="split-payment-modal-body">
            <p style="font-size: 16px; margin-bottom: 20px;">
              Your payment has been split into ${response.draftOrders.length} separate orders.
              You will now be redirected to complete each payment.
            </p>

            <div style="background: #f7f7f7; border-radius: 8px; padding: 15px; margin-bottom: 20px;">
              ${response.draftOrders.map(order => `
                <div style="display: flex; justify-content: space-between; align-items: center; padding: 10px 0; border-bottom: 1px solid #e0e0e0; ${order.splitNumber === response.draftOrders.length ? 'border-bottom: none;' : ''}">
                  <div>
                    <strong>Payment ${order.splitNumber} of ${order.totalSplits}</strong>
                    <br/>
                    <span style="color: #666; font-size: 14px;">${order.paymentProvider || order.method}</span>
                  </div>
                  <div style="text-align: right;">
                    <strong>${this.formatMoney(order.amount)}</strong>
                    <br/>
                    <span style="color: #666; font-size: 12px;">Order ${order.name || ''}</span>
                  </div>
                </div>
              `).join('')}
            </div>

            <div style="background: #FFF3CD; border-left: 4px solid #FFC107; padding: 10px; margin-bottom: 20px;">
              <strong>⚠️ Important:</strong><br/>
              Please complete all payments to finalize your order.<br/>
              Select <strong>${response.draftOrders[0]?.paymentProvider || response.draftOrders[0]?.method}</strong> for your first payment.
            </div>

            <div style="text-align: center;">
              <p style="color: #666; margin-bottom: 15px;">Redirecting to first payment in <span id="countdown">3</span> seconds...</p>
              <button id="proceed-to-payment" class="button button--primary" style="width: 100%; max-width: 300px;">
                Proceed to First Payment
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    // Add modal to page
    const modalContainer = document.createElement('div');
    modalContainer.innerHTML = modalHTML;
    document.body.appendChild(modalContainer);

    // Setup countdown and redirect
    let countdown = 3;
    const countdownElement = document.getElementById('countdown');
    const proceedButton = document.getElementById('proceed-to-payment');

    const countdownInterval = setInterval(() => {
      countdown--;
      if (countdownElement) {
        countdownElement.textContent = countdown;
      }
      if (countdown <= 0) {
        clearInterval(countdownInterval);
        // Set flag for auto-redirect on progress page
        sessionStorage.setItem('just_created_split', 'true');
        sessionStorage.setItem('current_split_group_id', response.groupId);

        // Always redirect to progress page, not directly to checkout
        window.location.href = `/pages/split-payment-progress?group_id=${response.groupId}`;
      }
    }, 1000);

    // Handle manual proceed button click
    proceedButton.addEventListener('click', () => {
      clearInterval(countdownInterval);
      // Set flag for auto-redirect on progress page
      sessionStorage.setItem('just_created_split', 'true');
      sessionStorage.setItem('current_split_group_id', response.groupId);

      // Redirect to the progress page instead of directly to invoice
      // The progress page will auto-redirect to the first invoice with minimal delay
      window.location.href = `/pages/split-payment-progress?group_id=${response.groupId}`;
    });

    // Handle modal close (if user clicks overlay)
    const overlay = document.querySelector('.split-payment-modal-overlay');
    overlay.addEventListener('click', () => {
      clearInterval(countdownInterval);
      modalContainer.remove();
    });
  }

  async applyDiscountCode() {
    const input = document.getElementById('split-discount-code');
    const code = input.value.trim();

    if (!code) return;

    this.showDiscountMessage('Applying discount...', 'info');

    try {
      // Apply discount code to cart
      const formData = new FormData();
      formData.append('discount', code);

      const response = await fetch('/discount/' + code, {
        method: 'GET'
      });

      if (response.ok) {
        // Refresh cart data to get new totals
        const cartData = await this.getCartData();

        if (cartData.total_discount && cartData.total_discount > 0) {
          this.activeDiscountCode = code;
          this.discountAmount = cartData.total_discount / 100;
          this.cartTotal = cartData.total_price / 100;

          this.displayActiveDiscount(code);
          this.updateDiscountDisplay();
          this.showDiscountMessage('Discount applied successfully!', 'success');

          // Clear input
          input.value = '';

          // Update all split amounts if auto-calculated
          if (this.numberOfSplits > 0) {
            this.updateSplitPreview();
          }
        } else {
          this.showDiscountMessage('Invalid or expired discount code', 'error');
        }
      } else {
        this.showDiscountMessage('Invalid discount code', 'error');
      }
    } catch (error) {
      console.error('Error applying discount:', error);
      this.showDiscountMessage('Failed to apply discount', 'error');
    }
  }

  async removeDiscountCode() {
    this.showDiscountMessage('Removing discount...', 'info');

    try {
      // Remove discount from cart
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: '' })
      });

      if (response.ok) {
        // Refresh cart data
        const cartData = await this.getCartData();
        this.cartTotal = cartData.total_price / 100;
        this.discountAmount = 0;
        this.activeDiscountCode = null;

        // Hide discount display
        document.getElementById('active-discount').style.display = 'none';
        document.getElementById('discount-amount-display').style.display = 'none';

        // Update cart total display
        document.getElementById('split-cart-total').textContent = this.formatMoney(this.cartTotal);

        this.showDiscountMessage('Discount removed', 'success');

        // Update splits if needed
        if (this.numberOfSplits > 0) {
          this.updateSplitPreview();
        }
      }
    } catch (error) {
      console.error('Error removing discount:', error);
      this.showDiscountMessage('Failed to remove discount', 'error');
    }
  }

  async updateCartNote(note) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          note: note
        })
      });

      if (response.ok) {
        console.log('[Split Payment V2] Cart note updated successfully');
        return true;
      } else {
        console.error('[Split Payment V2] Failed to update cart note');
        return false;
      }
    } catch (error) {
      console.error('[Split Payment V2] Error updating cart note:', error);
      return false;
    }
  }

  displayActiveDiscount(code) {
    const activeDiscountDiv = document.getElementById('active-discount');
    const discountText = activeDiscountDiv.querySelector('.discount-code-text');

    discountText.textContent = code.toUpperCase();
    activeDiscountDiv.style.display = 'block';

    // Hide input since discount is active
    document.getElementById('split-discount-code').value = '';
    document.getElementById('split-discount-code').placeholder = 'Discount applied';
  }

  updateDiscountDisplay() {
    const discountDisplay = document.getElementById('discount-amount-display');
    const discountAmountSpan = document.getElementById('discount-amount');
    const finalTotalSpan = document.getElementById('final-total');

    if (this.discountAmount > 0) {
      if (discountAmountSpan) {
        discountAmountSpan.textContent = this.formatMoney(this.discountAmount);
      }
      if (finalTotalSpan) {
        finalTotalSpan.textContent = this.formatMoney(this.cartTotal);
      }
      if (discountDisplay) {
        discountDisplay.style.display = 'block';
      }

      // Also update modal header totals if they exist
      const splitDiscountRow = document.querySelector('.split-discount-row');
      const splitDiscountAmount = document.getElementById('split-discount-amount');
      const splitFinalTotal = document.getElementById('split-final-total');

      if (splitDiscountRow) {
        splitDiscountRow.style.display = 'block';
      }
      if (splitDiscountAmount) {
        splitDiscountAmount.textContent = '-' + this.formatMoney(this.discountAmount);
      }
      if (splitFinalTotal) {
        splitFinalTotal.textContent = this.formatMoney(this.cartTotal);
      }
    } else {
      if (discountDisplay) {
        discountDisplay.style.display = 'none';
      }
    }
  }

  showDiscountMessage(message, type) {
    const messageDiv = document.getElementById('discount-message');
    messageDiv.textContent = message;
    messageDiv.className = 'discount-message ' + type;
    messageDiv.style.display = 'block';

    if (type !== 'info') {
      setTimeout(() => {
        messageDiv.style.display = 'none';
      }, 3000);
    }
  }

  showLoading() {
    const proceedBtn = document.getElementById('proceed-split-btn');
    proceedBtn.disabled = true;
    proceedBtn.textContent = 'Creating split orders...';
  }

  hideLoading() {
    const proceedBtn = document.getElementById('proceed-split-btn');
    proceedBtn.disabled = false;
    proceedBtn.textContent = 'Proceed with Split Payment';
  }

  async pollForCallbackData(groupId, maxAttempts = 20) {
    console.log('[Split Payment V2] Querying Shopify for draft orders, groupId:', groupId);
    // Query Shopify via Cloudflare for draft orders
    const queryUrl = 'https://split-payment-proxy.ainnlondon.workers.dev/query-orders?groupId=' + groupId;
    let attempts = 0;

    const poll = async () => {
      attempts++;
      try {
        console.log('[Split Payment V2] Query attempt ' + attempts + ', URL:', queryUrl);
        const response = await fetch(queryUrl);
        console.log('[Split Payment V2] Response status:', response.status);

        const data = await response.json();
        console.log('[Split Payment V2] Response data:', data);

        // Check if Shopify returned draft orders
        if (data.success && data.draftOrders && data.draftOrders.length > 0) {
          console.log('[Split Payment V2] ✅ Found', data.draftOrders.length, 'draft orders in Shopify');
          return data;
        }

        // If no orders found yet and we haven't reached max attempts
        if ((!data.success || data.draftOrders?.length === 0) && attempts < maxAttempts) {
          // First few attempts wait shorter, then increase wait time
          const waitTime = attempts < 3 ? 2000 : 3000;
          console.log('[Split Payment V2] No orders found yet, attempt ' + attempts + '/' + maxAttempts + ', waiting ' + (waitTime/1000) + ' seconds...');
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return poll();
        }

        console.log('[Split Payment V2] ❌ Max attempts reached or no orders found:', data);
        return null;
      } catch (error) {
        console.error('[Split Payment V2] ❌ Polling error on attempt', attempts + ':', error);
        if (attempts < maxAttempts) {
          console.log('[Split Payment V2] Retrying after error...');
          await new Promise(resolve => setTimeout(resolve, 2000));
          return poll();
        }
        return null;
      }
    };

    return poll();
  }
}

// Initialize when DOM is ready
function initSplitPaymentV2() {
  console.log('[Split Payment V2] DOM Ready - Initializing system');
  window.splitPaymentV2 = new SplitPaymentSystemV2();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSplitPaymentV2);
} else {
  // DOM already loaded
  initSplitPaymentV2();
}